import { CartAddEvent } from '@theme/events';
import { pushAddToCart } from '@theme/data-layer-helper';

class ComplimentaryProductBlock extends HTMLElement {
  connectedCallback() {
    this.addToCartButton = this.querySelector('[data-add-button]');
    this.plusIcon = this.querySelector('[data-icon-plus]');
    this.checkIcon = this.querySelector('[data-icon-check]');
    this.statusText = this.querySelector('[data-status-text]');
    this.buttonText = this.querySelector('[data-button-text]');
    this.productId = this.dataset.productId;
    
    this.addToCartButton?.addEventListener('click', this.#handleClick);
    document.addEventListener('cart:update', this.#handleCartUpdate);
  }

  disconnectedCallback() {
    this.addToCartButton?.removeEventListener('click', this.#handleClick);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
  }

  #handleCartUpdate = async (event) => {
    if (!this.productId) return;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const isInCart = cart.items.some(item => item.product_id === parseInt(this.productId));
      
      if (!isInCart && this.addToCartButton.getAttribute('aria-disabled') === 'true') {
        this.#resetButton();
      }
    } catch (error) {
      console.error('Error checking cart:', error);
    }
  };

  #resetButton() {
    this.addToCartButton.removeAttribute('aria-disabled');
    this.addToCartButton.style.pointerEvents = '';
    this.addToCartButton.style.opacity = '';
    this.addToCartButton.classList.remove('bg-toms-cream');
    
    this.plusIcon?.classList.remove('hidden');
    this.checkIcon?.classList.add('hidden');
    
    if (this.statusText) {
      this.statusText.textContent = `Add ${this.statusText.textContent.replace('Added to cart', '').replace('Added', '')} to cart`.trim();
    }
    
    if (this.buttonText) {
      this.buttonText.textContent = 'Add to cart';
    }
  }

  #handleClick = async (event) => {
    event.preventDefault();
    
    if (this.addToCartButton.getAttribute('aria-disabled') === 'true') {
      return;
    }

    const variantId = this.addToCartButton.dataset.variantId;
    const quantity = parseInt(this.addToCartButton.dataset.quantity) || 1;

    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    this.addToCartButton.setAttribute('aria-disabled', 'true');
    this.addToCartButton.style.pointerEvents = 'none';
    this.addToCartButton.style.opacity = '0.5';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity.toString());

      // Add sections parameter to get updated cart HTML
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      let cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      });
      if (cartItemComponentsSectionIds.length > 0) {
        formData.append('sections', cartItemComponentsSectionIds.join(','));
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Get metafields from data attribute
      let metafields = {};
      try {
        const metafieldsData = this.dataset.productMetafields;
        if (metafieldsData) {
          metafields = JSON.parse(metafieldsData);
        }
      } catch (e) {
        console.error('Failed to parse product metafields:', e);
      }

      // Push to dataLayer using the cart response item and metafields
      pushAddToCart(data, metafields);

      this.plusIcon?.classList.add('hidden');
      this.checkIcon?.classList.remove('hidden');
      
      if (this.statusText) {
        this.statusText.textContent = 'Added to cart';
      }
      
      if (this.buttonText) {
        this.buttonText.textContent = 'Added';
      }

      this.addToCartButton?.classList.add('bg-toms-cream');

      const cartDrawer = document.querySelector('cart-drawer-component');
      const hadAutoOpen = cartDrawer?.hasAttribute('auto-open');
      
      if (cartDrawer && !hadAutoOpen) {
        cartDrawer.setAttribute('auto-open', '');
      }
      if (data) {
        document.dispatchEvent(
          new CartAddEvent(data, variantId, {
            source: 'complimentary-product-block',
            itemCount: quantity,
            productId: this.productId,
            sections: data.sections
          })
        );
      }

      if (cartDrawer && !hadAutoOpen) {
        setTimeout(() => {
          cartDrawer.removeAttribute('auto-open');
        }, 500);
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      
      if (this.addToCartButton) {
        this.addToCartButton.removeAttribute('aria-disabled');
        this.addToCartButton.style.pointerEvents = '';
        this.addToCartButton.style.opacity = '';
      }
      
      if (this.statusText) {
        this.statusText.textContent = 'Error adding to cart. Please try again.';
      }
    }
  };
}

if (!customElements.get('complimentary-product-block')) {
  customElements.define('complimentary-product-block', ComplimentaryProductBlock);
}
